import { Box, HStack, Icon, Link, Text, VStack } from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { Link as RouterLink } from "../../components";
import { DRIP_TEXT_MUTED } from "./dripStyles";

// The SILV rewards campaign, suspended after SILV lost its peg. The page it ran
// on is gone, but wallets that pointed their Drip at SILV are still dripping it,
// so they get a standing note explaining where the campaign went.
//
// Self-contained on purpose: when the campaign is written off for good, deleting
// this file and its one render site in DripPage removes every trace of it.
const SILV_MINT = "SiLVFMgD3eD2rgK628NbTBq9MnuJF5FW2CRaVyTB35L";
const DOMINION_X_URL = "https://x.com/Dominion_Market";

// Matches <DripAlerts />: these notes are informational, never a warning.
const INFO_STYLE = {
  bg: "rgba(95, 244, 244, 0.06)",
  border: "rgba(95, 244, 244, 0.3)",
  iconColor: "#5FF4F4",
};

/** Does this wallet drip a token whose campaign was suspended? */
export const hasSuspendedCampaignToken = (rewardTokenMints: string[]): boolean =>
  rewardTokenMints.includes(SILV_MINT);

interface SuspendedCampaignNoticeProps {
  /** Reward tokens this wallet is currently dripping, across every vault. */
  rewardTokenMints: string[];
  /**
   * Where to go to pick a different reward token. Partner pages lock the reward
   * token, so they link out to /drip; the base page has the picker below and
   * needs no link.
   */
  changeTokenHref?: string;
}

export const SuspendedCampaignNotice = ({
  rewardTokenMints,
  changeTokenHref,
}: SuspendedCampaignNoticeProps) => {
  if (!hasSuspendedCampaignToken(rewardTokenMints)) return null;

  return (
    <Box
      p={3}
      borderRadius="xl"
      border="1px solid"
      borderColor={INFO_STYLE.border}
      bg={INFO_STYLE.bg}
    >
      <HStack spacing={3} align="flex-start">
        <Icon
          as={InfoOutlineIcon}
          color={INFO_STYLE.iconColor}
          boxSize={4}
          mt="2px"
          flexShrink={0}
        />
        <VStack spacing={1} align="stretch">
          <Text fontSize="sm" color="white">
            The SILV rewards campaign is suspended following the SILV depeg. Your
            deposit is unaffected: your Drip keeps buying SILV each epoch, so you
            can stay in it or{" "}
            {changeTokenHref ? (
              <RouterLink
                to={changeTokenHref}
                color={INFO_STYLE.iconColor}
                fontWeight="semibold"
                textDecoration="underline"
              >
                change your reward token
              </RouterLink>
            ) : (
              "change your reward token below"
            )}
            .
          </Text>
          <Text fontSize="xs" color={DRIP_TEXT_MUTED}>
            Latest status from Dominion:{" "}
            <Link
              href={DOMINION_X_URL}
              isExternal
              color={DRIP_TEXT_MUTED}
              textDecoration="underline"
            >
              @Dominion_Market ↗
            </Link>
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};
